import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useProductDetails } from '@/lib/api/products';
import { Product } from '@tagadapay/crm-entities';
import ProductDetailsSkeleton from './components/skeleton';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { ProductHeader } from './components/product-header';
import { ProductOverviewCard } from './components/product-overview-card';
import { ProductDetailsTab } from './components/product-details-tab';
import { ProductVariantsTab } from './components/product-variants-tab';

interface ProductDetailPageProps {
  product: Product.ProductWithRelations;
}

const ProductDetailPageContainer = () => {
  const params = useParams();
  const productId = params.productId!;
  const { t } = useTranslation('products');
  const { data, isLoading, error } = useProductDetails([productId]);

  if (isLoading) {
    return <ProductDetailsSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="container mx-auto py-8">
        <p>{t('productNotFound')}</p>
      </div>
    );
  }

  return <ProductDetailPage product={data[0]} />;
};

const ProductDetailPage = ({ product }: ProductDetailPageProps) => {
  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <ProductHeader product={product} />

        {/* Main Content */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main content - 2/3 width */}
          <div className="space-y-6 lg:col-span-2">
            <ProductVariantsTab product={product} />
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-6">
            <ProductDetailsTab product={product} showSettings={true} showInfo={true} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRequiredPermission(ProductDetailPageContainer, PERMISSIONS.STORES_VIEW);
